import React, { useState, useEffect } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import {
  Layers,
  Calendar,
  X,
  Share2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Video,
  ExternalLink,
  Copy,
  Facebook,
  CheckCircle,
  Tag,
  Filter,
  UserCheck,
  Download
} from "lucide-react";

const ParentGallery = () => {
  // State variables
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [media, setMedia] = useState([]);
  
  // Loading states
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);
  
  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Share tray
  const [showShareIndex, setShowShareIndex] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Filter Mode: "all", "class", or "tagged"
  const [filterMode, setFilterMode] = useState("all");

  // Load events
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch media when event or filterMode changes
  useEffect(() => {
    if (selectedEvent) {
      fetchEventMedia(selectedEvent.id, filterMode);
    } else {
      setMedia([]);
    }
  }, [selectedEvent, filterMode]);

  // API Call: Fetch all events matching parent's children
  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await apiClient.get("/v1/parent/gallery/events");
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Error fetching parent events:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  // API Call: Fetch media inside event
  const fetchEventMedia = async (eventId, mode) => {
    setLoadingMedia(true);
    try {
      const response = await apiClient.get(
        `/v1/parent/gallery/events/${eventId}`,
        {
          params: { 
            only_my_children: mode === "tagged",
            only_my_class: mode === "class"
          },
        }
      );
      setMedia(response.data.media || []);
    } catch (error) {
      console.error("Error fetching event media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  // Fullscreen view lightbox controls
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const prevLightboxItem = () => {
    setLightboxIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const nextLightboxItem = () => {
    setLightboxIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  // Download Media File via Universal Downloader
  const handleDownload = async (mediaItem) => {
    try {
      const response = await apiClient.get(`/v1/parent/gallery/media/${mediaItem.id}/download`, {
        responseType: "blob"
      });
      const mimeType = response.headers?.["content-type"] || "image/jpeg";
      await downloadFile(response.data, mediaItem.original_name, mimeType);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file.");
    }
  };

  // Share Tray Actions
  const copyShareLink = (url, index) => {
    navigator.clipboard.writeText(url);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const shareSocial = (platform, url, title = "School Event Gallery") => {
    const text = encodeURIComponent(`Check out this photo from the school event: ${title}!`);
    const link = encodeURIComponent(url);
    let shareUrl = "";

    if (platform === "facebook") {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${link}`;
    } else if (platform === "twitter") {
      shareUrl = `https://twitter.com/intent/tweet?url=${link}&text=${text}`;
    } else if (platform === "whatsapp") {
      shareUrl = `https://api.whatsapp.com/send?text=${text}%20${link}`;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  return (
    <div className="space-y-6">
      {/* Upper Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Layers className="w-6 h-6 text-sky-500" />
            School Event Gallery
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Browse through latest school events, assemblies, sports events, and view moments featuring your child.
          </p>
        </div>
        {selectedEvent && (
          <Button
            variant="outline"
            onClick={() => {
              setSelectedEvent(null);
              setFilterMode("all");
            }}
            className="border-slate-200 hover:bg-slate-50 text-slate-700 self-start"
          >
            Back to Albums
          </Button>
        )}
      </div>

      {/* Main Container */}
      {!selectedEvent ? (
        // Event Grid View
        loadingEvents ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-16 text-center shadow-sm">
            <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No Gallery Albums Yet</h3>
            <p className="text-slate-500 max-w-sm mx-auto mt-2 text-sm">
              There are no gallery events configured for your child's class yet. Check back later for updates!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md hover:border-sky-100 transition-all group flex flex-col justify-between cursor-pointer"
              >
                <div className="relative aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                  {event.cover_image_url ? (
                    <img
                      src={event.cover_image_url}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-slate-400">
                      <Layers className="w-10 h-10 stroke-1 mb-2" />
                      <span className="text-xs">No media uploaded</span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-black/45 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                    <Calendar className="w-3.5 h-3.5" />
                    {event.date}
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-bold text-slate-800 text-lg group-hover:text-sky-500 transition-colors line-clamp-1">
                    {event.title}
                  </h3>
                  <p className="text-slate-500 text-sm mt-2 line-clamp-2">
                    {event.description || "No description provided."}
                  </p>
                  {event.tags && event.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {event.tags.map((tag) => (
                        <span
                          key={tag}
                          className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // Event Gallery Details view with tags filtering
        <div className="space-y-6">
          {/* Album Title Header & Personalization Toggle */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-slate-800">{selectedEvent.title}</h2>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                  {selectedEvent.date}
                </span>
              </div>
              <p className="text-slate-500 text-sm mt-1">{selectedEvent.description}</p>
            </div>

            {/* Smart personalization toggle */}
            <div className="flex bg-slate-100 p-1 rounded-xl self-start md:self-center">
              <button
                onClick={() => setFilterMode("all")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  filterMode === "all"
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                All Event Media
              </button>
              <button
                onClick={() => setFilterMode("class")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  filterMode === "class"
                    ? "bg-indigo-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Tag className="w-3.5 h-3.5" />
                My Child's Class
              </button>
              <button
                onClick={() => setFilterMode("tagged")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  filterMode === "tagged"
                    ? "bg-sky-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <UserCheck className="w-3.5 h-3.5" />
                Photos of My Child
              </button>
            </div>
          </div>

          {/* Media Items Grid */}
          {loadingMedia ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
            </div>
          ) : media.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-2xl py-20 text-center text-slate-400">
              <UserCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">No media found matching this filter.</p>
              {filterMode === "tagged" && (
                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                  Either your child is not tagged in this album yet, or the wardens haven't tagged student names.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {media.map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => openLightbox(index)}
                  className="relative rounded-xl overflow-hidden aspect-square bg-slate-900 group border border-slate-100 cursor-pointer shadow-sm hover:scale-[1.02] hover:shadow-md transition-all duration-200"
                >
                  {item.media_type === "video" ? (
                    <div className="w-full h-full relative flex items-center justify-center">
                      <img
                        src={item.thumbnail_link}
                        alt={item.original_name}
                        className="w-full h-full object-cover opacity-80"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white">
                          <Video className="w-5 h-5 fill-white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.thumbnail_link}
                      alt={item.original_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  )}

                  {/* Badges for Tagged Children */}
                  {item.is_child_tagged ? (
                    <div className="absolute top-2.5 left-2.5 bg-emerald-500/95 backdrop-blur-sm text-white text-[10px] px-2.5 py-0.5 rounded-full flex items-center gap-1 font-semibold shadow-sm z-10 animate-in fade-in duration-200">
                      <Tag className="w-3 h-3 animate-pulse" />
                      Featuring {item.tagged_children_names.join(", ")}
                    </div>
                  ) : item.is_class_tagged ? (
                    <div className="absolute top-2.5 left-2.5 bg-indigo-500/95 backdrop-blur-sm text-white text-[10px] px-2.5 py-0.5 rounded-full flex items-center gap-1 font-semibold shadow-sm z-10 animate-in fade-in duration-200">
                      <Tag className="w-3 h-3" />
                      Class Photo
                    </div>
                  ) : null}

                  {/* Overlays / Hover Buttons */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                    <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1.5 w-full justify-between">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openLightbox(index);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-xs text-white transition-colors font-medium"
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                          View
                        </button>
                        
                        <div className="relative flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(item);
                            }}
                            className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                            title="Download"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowShareIndex(showShareIndex === index ? null : index);
                            }}
                            className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                            title="Share"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                          {showShareIndex === index && (
                            <div className="absolute bottom-8 right-0 bg-slate-900 border border-slate-800 rounded-xl p-2.5 shadow-xl flex gap-1.5 z-10">
                              <button
                                onClick={() => copyShareLink(item.web_view_link, index)}
                                className="p-1.5 bg-white/5 hover:bg-white/15 text-white rounded-lg"
                                title="Copy Link"
                              >
                                {copiedIndex === index ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => shareSocial("whatsapp", item.web_view_link, selectedEvent.title)}
                                className="p-1.5 bg-green-600/30 hover:bg-green-600 text-green-400 rounded-lg"
                                title="Share on WhatsApp"
                              >
                                <span className="text-[10px] font-bold px-1 text-white">WA</span>
                              </button>
                              <button
                                onClick={() => shareSocial("facebook", item.web_view_link, selectedEvent.title)}
                                className="p-1.5 bg-blue-600/30 hover:bg-blue-600 text-blue-400 rounded-lg"
                                title="Share on Facebook"
                              >
                                <Facebook className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox Overlay */}
      {lightboxOpen && media.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-[9999] flex flex-col justify-between p-4">
          <div className="flex justify-between items-center text-white">
            <span className="text-sm font-semibold">
              {lightboxIndex + 1} / {media.length} — {media[lightboxIndex].original_name}
            </span>
            <div className="flex gap-4">
              <button
                onClick={() => handleDownload(media[lightboxIndex])}
                className="text-slate-400 hover:text-white p-2"
                title="Download High-Res"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={() => setLightboxOpen(false)}
                className="text-slate-400 hover:text-white p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center relative my-4">
            <button
              onClick={prevLightboxItem}
              className="absolute left-2 text-white/50 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {media[lightboxIndex].media_type === "video" ? (
              <video
                src={media[lightboxIndex].web_content_link}
                controls
                autoPlay
                className="max-h-[80vh] max-w-[85vw] object-contain rounded"
              />
            ) : (
              <img
                src={media[lightboxIndex].web_view_link.replace("/view", "/uc?export=view")} // direct stream fallback
                onError={(e) => {
                  e.target.src = media[lightboxIndex].web_content_link; // fallback to download stream link
                }}
                alt={media[lightboxIndex].original_name}
                className="max-h-[80vh] max-w-[85vw] object-contain rounded"
              />
            )}

            <button
              onClick={nextLightboxItem}
              className="absolute right-2 text-white/50 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div className="text-center text-slate-500 text-xs">
            School Gallery Viewer
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentGallery;
